import { useEffect, useRef } from "react";
import { useAPI } from "@huub-web/backend";
import type { InteriorDesignerAction } from "../context/types";
import type { BackendGetPhoto } from "./usePhotoLoader";
import { mapClassification } from "./usePhotoLoader";

/**
 * Fetches the full photo when navigating between history nodes.
 * Dispatches ACTIVE_PHOTO_FETCHED on success or NAVIGATION_FAILED on error.
 */
export function usePhotoNavigation({
  isNavigating,
  activeNodeId,
  dispatch,
}: {
  /**
   * Whether a navigation is currently in progress
   */
  isNavigating: boolean;
  /**
   * The node ID being navigated to
   */
  activeNodeId: string;
  /**
   * Reducer dispatch function
   */
  dispatch: (action: InteriorDesignerAction) => void;
}) {
  const { $get } = useAPI();
  const $getRef = useRef($get);
  $getRef.current = $get;

  useEffect(() => {
    if (!isNavigating) return;

    let cancelled = false;

    $getRef
      .current(`/interior-designer/photo/${activeNodeId}`)
      .then((response: { data: { photo: BackendGetPhoto } }) => {
        if (cancelled) return;
        const { photo } = response.data;
        dispatch({
          type: "ACTIVE_PHOTO_FETCHED",
          blobUrl: photo.blob_url ?? photo.source_url ?? "",
          classification: mapClassification({ photo }),
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error(
          "[photo-navigation] Failed to fetch navigated photo:",
          error,
        );
        dispatch({ type: "NAVIGATION_FAILED" });
      });

    return () => {
      cancelled = true;
    };
  }, [isNavigating, activeNodeId, dispatch]);
}
